import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FIRE_COLORS } from '../BlazeMaterial';

// METEOR TRAIL - fiery comet streak down the path
const MeteorTrail = ({ x, startY, endY, delay }) => {
  const trailRef = useRef();
  const coreRef = useRef();
  const startTime = useRef(null);
  const trailHeight = Math.abs(startY - endY);

  useFrame((state) => {
    if (!trailRef.current || !coreRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current - delay;

    if (elapsed <= 0 || elapsed > 0.25) {
      trailRef.current.visible = false;
      coreRef.current.visible = false;
      return;
    }

    trailRef.current.visible = true;
    coreRef.current.visible = true;

    const progress = elapsed / 0.25;

    // Trail fades from bottom to top (meteor passed through)
    const fadeOut = 1 - progress;
    trailRef.current.material.opacity = fadeOut * 0.7;
    trailRef.current.scale.x = 0.4 + fadeOut * 0.3;

    // Bright core at the bottom (impact point)
    coreRef.current.material.opacity = fadeOut * 0.9;
    coreRef.current.scale.setScalar(0.3 + fadeOut * 0.2);
    coreRef.current.material.emissiveIntensity = 2 * fadeOut;
  });

  return (
    <group position={[x, endY + trailHeight / 2, 0.1]}>
      {/* Fire trail */}
      <mesh ref={trailRef} visible={false}>
        <planeGeometry args={[0.6, trailHeight]} />
        <meshBasicMaterial
          color={FIRE_COLORS.flame}
          transparent
          opacity={0.7}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Hot core */}
      <mesh ref={coreRef} position={[0, -trailHeight / 2 + 0.3, 0.05]} visible={false}>
        <circleGeometry args={[0.25, 12]} />
        <meshStandardMaterial
          color={FIRE_COLORS.flameCore}
          emissive={FIRE_COLORS.ember}
          emissiveIntensity={2}
          transparent
          opacity={0.9}
        />
      </mesh>
    </group>
  );
};

// IMPACT CRATER - spreading ring of fire at landing
const ImpactCrater = ({ x, y, delay }) => {
  const craterRef = useRef();
  const innerRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!craterRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current - delay;

    if (elapsed <= 0 || elapsed > 0.4) {
      craterRef.current.visible = false;
      if (innerRef.current) innerRef.current.visible = false;
      return;
    }

    craterRef.current.visible = true;
    if (innerRef.current) innerRef.current.visible = elapsed < 0.2;

    const progress = elapsed / 0.4;
    const easeOut = 1 - Math.pow(1 - progress, 2);

    // Expanding crater ring
    const scale = 0.3 + easeOut * 1.2;
    craterRef.current.scale.set(scale, scale * 0.4, 1);
    craterRef.current.material.opacity = (1 - progress) * 0.8;

    // Inner hot core
    if (innerRef.current && elapsed < 0.2) {
      const innerProgress = elapsed / 0.2;
      innerRef.current.scale.setScalar(0.4 * (1 - innerProgress));
      innerRef.current.material.opacity = (1 - innerProgress) * 0.9;
    }
  });

  return (
    <group position={[x, y, 0.15]}>
      {/* Crater ring */}
      <mesh ref={craterRef} visible={false}>
        <ringGeometry args={[0.2, 0.4, 16]} />
        <meshBasicMaterial
          color={FIRE_COLORS.ember}
          transparent
          opacity={0.8}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Inner hot spot */}
      <mesh ref={innerRef} position={[0, 0, 0.02]} visible={false}>
        <circleGeometry args={[0.3, 12]} />
        <meshBasicMaterial
          color={FIRE_COLORS.flameCore}
          transparent
          opacity={0.9}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
};

// SMOKE PLUME - rising smoke from impact
const SmokePlume = ({ x, y, delay }) => {
  const smokesRef = useRef([]);
  const startTime = useRef(null);

  const smokeData = useMemo(() => {
    const data = [];
    for (let i = 0; i < 5; i++) {
      data.push({
        offsetX: (Math.random() - 0.5) * 0.3,
        speed: 1.5 + Math.random() * 1,
        size: 0.15 + Math.random() * 0.1,
        delay: i * 0.03,
        drift: (Math.random() - 0.5) * 0.5,
      });
    }
    return data;
  }, []);

  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current - delay;

    smokesRef.current.forEach((smoke, i) => {
      if (!smoke) return;
      const data = smokeData[i];
      const adjustedTime = elapsed - data.delay;

      if (adjustedTime <= 0 || adjustedTime > 0.6) {
        smoke.visible = false;
        return;
      }

      smoke.visible = true;
      const progress = adjustedTime / 0.6;

      // Rise and expand
      smoke.position.y = progress * data.speed;
      smoke.position.x = data.offsetX + Math.sin(adjustedTime * 3) * data.drift * progress;
      smoke.scale.setScalar(data.size * (1 + progress * 2));

      // Fade out
      smoke.material.opacity = (1 - progress) * 0.4;
    });
  });

  return (
    <group position={[x, y + 0.3, 0.12]}>
      {smokeData.map((data, i) => (
        <mesh
          key={i}
          ref={(el) => smokesRef.current[i] = el}
          position={[data.offsetX, 0, 0]}
          visible={false}
        >
          <circleGeometry args={[data.size, 8]} />
          <meshBasicMaterial
            color={FIRE_COLORS.smoke}
            transparent
            opacity={0.4}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

// FIRE SPARKS - scattered sparks flying from impact
const FireSparks = ({ x, y, delay }) => {
  const sparksRef = useRef([]);
  const startTime = useRef(null);

  const sparkData = useMemo(() => {
    const data = [];
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      data.push({
        angle,
        speed: 2 + Math.random() * 2,
        size: 0.04 + Math.random() * 0.03,
        upward: 0.5 + Math.random() * 1,
      });
    }
    return data;
  }, []);

  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current - delay;

    if (elapsed <= 0 || elapsed > 0.35) {
      sparksRef.current.forEach(s => { if (s) s.visible = false; });
      return;
    }

    const progress = elapsed / 0.35;

    sparksRef.current.forEach((spark, i) => {
      if (!spark) return;
      const data = sparkData[i];

      spark.visible = true;

      // Fly outward and upward
      const distance = data.speed * progress;
      spark.position.x = Math.cos(data.angle) * distance;
      spark.position.y = Math.sin(data.angle) * distance * 0.5 + data.upward * progress;

      // Shrink and fade
      spark.scale.setScalar(data.size * (1 - progress * 0.7));
      const flicker = Math.sin(elapsed * 40 + i) * 0.3 + 0.7;
      spark.material.opacity = (1 - progress) * flicker;
    });
  });

  return (
    <group position={[x, y + 0.3, 0.18]}>
      {sparkData.map((data, i) => (
        <mesh
          key={i}
          ref={(el) => sparksRef.current[i] = el}
          visible={false}
        >
          <circleGeometry args={[data.size, 5]} />
          <meshBasicMaterial
            color={i % 2 === 0 ? FIRE_COLORS.spark : FIRE_COLORS.ember}
            transparent
            opacity={0.9}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

// GROUND FIRE - flames spreading along ground from impact
const GroundFire = ({ x, y, delay }) => {
  const flamesRef = useRef([]);
  const startTime = useRef(null);

  const flameData = useMemo(() => {
    const data = [];
    for (let i = 0; i < 4; i++) {
      const side = i < 2 ? -1 : 1;
      const index = i % 2;
      data.push({
        offsetX: side * (0.2 + index * 0.25),
        height: 0.2 + Math.random() * 0.15,
        delay: Math.abs(index) * 0.04,
        phase: Math.random() * Math.PI * 2,
      });
    }
    return data;
  }, []);

  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current - delay;

    flamesRef.current.forEach((flame, i) => {
      if (!flame) return;
      const data = flameData[i];
      const adjustedTime = elapsed - data.delay;

      if (adjustedTime <= 0 || adjustedTime > 0.4) {
        flame.visible = false;
        return;
      }

      flame.visible = true;
      const progress = adjustedTime / 0.4;

      // Flame rises then shrinks
      const riseProgress = progress < 0.3 ? progress / 0.3 : 1;
      const fadeProgress = progress > 0.3 ? (progress - 0.3) / 0.7 : 0;

      flame.scale.y = data.height * riseProgress * (1 - fadeProgress * 0.7);
      flame.scale.x = 0.12 * (1 - fadeProgress * 0.5);
      flame.position.y = flame.scale.y / 2;

      // Flickering
      const flicker = Math.sin(adjustedTime * 25 + data.phase) * 0.2 + 0.8;
      flame.material.opacity = (1 - fadeProgress) * 0.8 * flicker;
    });
  });

  return (
    <group position={[x, y, 0.15]}>
      {flameData.map((data, i) => (
        <mesh
          key={i}
          ref={(el) => flamesRef.current[i] = el}
          position={[data.offsetX, 0, 0]}
          visible={false}
        >
          <coneGeometry args={[0.06, 0.25, 6]} />
          <meshBasicMaterial
            color={i % 2 === 0 ? FIRE_COLORS.flame : FIRE_COLORS.ember}
            transparent
            opacity={0.8}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

// SHOCKWAVE - expanding ring shockwave
const Shockwave = ({ x, y, delay }) => {
  const waveRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!waveRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current - delay;

    if (elapsed <= 0 || elapsed > 0.3) {
      waveRef.current.visible = false;
      return;
    }

    waveRef.current.visible = true;
    const progress = elapsed / 0.3;
    const easeOut = 1 - Math.pow(1 - progress, 3);

    // Expand rapidly
    const scale = 0.2 + easeOut * 2;
    waveRef.current.scale.set(scale, scale * 0.3, 1);
    waveRef.current.material.opacity = (1 - progress) * 0.5;
  });

  return (
    <mesh ref={waveRef} position={[x, y, 0.08]} visible={false}>
      <ringGeometry args={[0.5, 0.6, 24]} />
      <meshBasicMaterial
        color={FIRE_COLORS.heatDistort}
        transparent
        opacity={0.5}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

const EmberHardDropTrail = ({ startY, endY, columns, onComplete }) => {
  const startTime = useRef(Date.now());

  // Calculate positions
  const impacts = useMemo(() => {
    return columns.map((col, i) => ({
      x: col - 5 + 0.5,
      y: endY + 0.5,
      delay: i * 0.015,
    }));
  }, [columns, endY]);

  useFrame(() => {
    const elapsed = (Date.now() - startTime.current) / 1000;

    if (elapsed > 0.5) {
      onComplete?.();
    }
  });

  return (
    <group>
      {/* Meteor trails for each column */}
      {columns.map((col, i) => (
        <MeteorTrail
          key={`trail-${i}`}
          x={col - 5 + 0.5}
          startY={startY}
          endY={endY}
          delay={i * 0.01}
        />
      ))}

      {/* Impact effects for each column */}
      {impacts.map((impact, i) => (
        <group key={`impact-${i}`}>
          {/* Shockwave */}
          <Shockwave x={impact.x} y={impact.y} delay={impact.delay} />

          {/* Impact crater */}
          <ImpactCrater x={impact.x} y={impact.y} delay={impact.delay} />

          {/* Ground fire */}
          <GroundFire x={impact.x} y={impact.y} delay={impact.delay + 0.02} />

          {/* Fire sparks */}
          <FireSparks x={impact.x} y={impact.y} delay={impact.delay} />

          {/* Smoke plume */}
          <SmokePlume x={impact.x} y={impact.y} delay={impact.delay + 0.05} />
        </group>
      ))}
    </group>
  );
};

export default EmberHardDropTrail;
